import { InlineMath } from "react-katex";
import i18n from "../../../language/i18n";
import { IActivityEvaluationRubricProps } from "../../../properties/ActivityProperties";
import {
  constructInputWithSpecialExpressionList,
  readActivityDataField,
} from "../../../utilization/CatchtivityUtilization";

const ActivityEvaluationRubricContent = ({
  activityTemplateType,
  data,
  hideTitleAndBorder = false,
}: IActivityEvaluationRubricProps & { hideTitleAndBorder?: boolean }) => {
  if (!activityTemplateType) return;

  // Dual-read: prefers flat evaluationRubricMap; falls back to legacy <type>EvaluationRubricMap.
  const evaluationRubricMapString = readActivityDataField(data, activityTemplateType, "evaluationRubricMap");
  let evaluationRubricMap;
  if (!evaluationRubricMapString) return;
  try {
    evaluationRubricMap = JSON.parse(evaluationRubricMapString);
  } catch (error) {
    console.error("Error parsing evaluation rubric map:", error);
    return null;
  }
  if (!evaluationRubricMap || Object.keys(evaluationRubricMap).length === 0)
    return null;

  const renderContent = () => (
    <>
      {Object.keys(evaluationRubricMap).map((key, index) => {
        const currentItem = JSON.parse(evaluationRubricMap[key]);
        const { value } = currentItem;
        // The persisted value carries the unit INSIDE the bracket — "[100 puan] …"
        // (admin-web's rubric editor writes `[N ${i18n.t("points")}]`, so it is
        // "puan" in TR and "Points" in EN). This used to match /\[(\d+)\]/ — digits
        // and a closing bracket, nothing between — which no stored rubric has ever
        // satisfied: every criterion fell back to points 0 and kept the
        // "[100 puan]" prefix inside its own text. Byte-identical to the editor's own
        // parseRubricValue on purpose, so writer and reader can never disagree about
        // the same stored string. The bare "[N]" form still parses.
        const matchedRegex = value.match(
          /^\[(\d+)\s*(?:points?|puan)?\]\s*([\s\S]*)$/i
        );
        let points = 0;
        let evaluationRubric = value;

        if (matchedRegex) {
          points = parseFloat(matchedRegex[1]);
          evaluationRubric = matchedRegex[2];
        }

        return (
          <div key={`${key}_${index}`} className="am-rub-row">
            {/* A criterion with 0 points is UNSCORED — the generator writes these on
                purpose (the "KURAL …" lines that steer the grader without carrying
                points). "0 Puan" beside a rubric whose points must add up to 100
                reads as a hole in it, so a zero says so in words instead. */}
            <div
              className={`am-rub-points ${
                points > 0 ? "" : "am-rub-points--unscored"
              }`}
            >
              {points > 0
                ? `${points} ${i18n.t("points")}`
                : i18n.t("unscored")}
            </div>
            <p className="am-rub-text">
              {constructInputWithSpecialExpressionList(evaluationRubric).map(
                (inputPart, inputIndex) => (
                  <span
                    key={inputIndex}
                    className={`${inputPart.isBold ? "am-bold" : ""} ${
                      inputPart.isUnderline ? "am-underline" : ""
                    }`}
                  >
                    {inputPart.isEquation ? (
                      <span className="am-eq">
                        <InlineMath math={inputPart.value} />
                      </span>
                    ) : (
                      inputPart.value
                    )}
                  </span>
                )
              )}
            </p>
          </div>
        );
      })}
    </>
  );

  if (hideTitleAndBorder) {
    return renderContent();
  }

  return (
    <div className="am-rub-box">
      <p className="am-section-title">{i18n.t("evaluation_rubric")}</p>
      {renderContent()}
    </div>
  );
};

export default ActivityEvaluationRubricContent;
